import { useState } from 'react';
import { useConnectionState, useMachineMode, useSendCommand, useUpdateStatus } from '@/store/store';
import { ConnectionState } from '@/types/machine.types';
import { AirIcon, SettingsIcon, UpdateIcon } from '@/icons';
import { SettingsModal } from '@/components/SettingsModal/SettingsModal';
import logo from '@/assets/logo.webp';
import styles from '@/components/StatusHeader/StatusHeader.module.css';

export function StatusHeader(): JSX.Element {
  const connectionState = useConnectionState();
  const machineMode = useMachineMode();
  const updateStatus = useUpdateStatus();
  const sendCommand = useSendCommand();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [airOn, setAirOn] = useState(false);

  // Air cooling toggle: M7 turns mist/air coolant on, M9 turns all coolant off.
  const toggleAir = (): void => {
    const next = !airOn;
    setAirOn(next);
    sendCommand({ type: 'MDI', command: next ? 'M7' : 'M9' });
  };
  const hasUpdate = updateStatus === 'available' || updateStatus === 'ready';

  const isConnected = connectionState === ConnectionState.CONNECTED;

  // Safety-first binary indicator: the chip is green ONLY when the UI is truly
  // driving the real machine (connected AND backend reports REAL). Every other
  // state — simulation, disconnected, awaiting first status — is "Offline", so
  // the operator never mistakes a non-real session for the real one.
  const isOnline = isConnected && machineMode === 'REAL';
  const mode: { cls: string | undefined; label: string } = isOnline
    ? { cls: styles.modeChipReal, label: 'Machine Online' }
    : { cls: styles.modeChipSim, label: 'Machine Offline' };

  return (
    <div className={styles.headerBar}>
      {/* Logo */}
      <img src={logo} alt="Rownd" className={styles.headerLogo} />

      {/* Update icon (only when available/ready) */}
      {hasUpdate && (
        <button
          className={styles.headerUpdate}
          type="button"
          onClick={() => setSettingsOpen(true)}
          aria-label={updateStatus === 'ready' ? 'Restart to install update' : 'Update available'}
        >
          <UpdateIcon width={17} height={17} aria-hidden="true" style={{ color: '#00BFFF' }} />
          <span className={styles.headerUpdateLabel}>New Update</span>
        </button>
      )}

      {/* Machine mode indicator (REAL vs SIMULATION) */}
      <div className={styles.headerChips}>
        <div className={`${styles.connectionChip} ${mode.cls}`}>
          <span className={styles.modeDot} />
          <span className={styles.connectionChipLabel}>{mode.label}</span>
        </div>
      </div>

      {/* Air cooling toggle */}
      <div className={styles.headerAir}>
        <AirIcon width={40} height={40} aria-hidden="true" style={{ color: '#B6B6C8' }} />
        <button
          type="button"
          role="switch"
          aria-checked={airOn}
          aria-label="Air cooling"
          className={`${styles.airToggle} ${airOn ? styles.airToggleOn : ''}`}
          onClick={toggleAir}
        >
          <span className={styles.airToggleKnob} />
        </button>
      </div>

      {/* Settings */}
      <button
        className={styles.headerSettings}
        type="button"
        aria-label="Settings"
        onClick={() => setSettingsOpen(true)}
      >
        <SettingsIcon width={64} height={64} style={{ color: '#B6B6C8' }} />
      </button>

      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}
